import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { Observable, OperatorFunction, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { PageableListResponse, PageLoadConfig } from '../infrastructure/page-controller';
import { ServiceBase } from '../infrastructure/service-base';
import { WebsocketService } from '../infrastructure/websocket-service';
import { RejectReason } from '../pages/violations/dialogs/reject-request-dialog/reject-request-dialog.component';
import { BackendService } from './backend.service';
import { UserDto } from './users.service';
import { Record } from './service-crud.service';

export interface ViolationsCounter {
  status: ViolationStatusFilter;
  count: number;
}

// eslint-disable-next-line no-shadow
export enum ViolationStatusFilter {
  all = 'all',
  created = 'created',
  isConsidered = 'isConsidered',
  accepted = 'accepted',
  rejected = 'rejected'
}

// eslint-disable-next-line no-shadow
export enum ViolationStatus {
  created = 'created',
  isConsidered = 'isConsidered',
  accepted = 'accepted',
  rejected = 'rejected'
}

// eslint-disable-next-line no-shadow
export enum ViolationType {
  industrialSafety = 'industrial-safety',
  details = 'details'
}

// eslint-disable-next-line no-shadow
export enum ViolationTypeFilter {
  industrialSafety = 'industrial-safety',
  details = 'details',
  all = 'all'
}
export type ViolationCameraFilter = string | 'all';
export type ViolationDepartmentFilter = string;
export interface ViolationDateRangeFilter {
  startDate: moment.Moment;
  endDate: moment.Moment;
}

export type MediaType = 'video' | 'image';

export interface ViolationMedia {
  type: MediaType;
  preview: string;
  data: string;
  datetime: Date;
}

interface ViolationMediaRaw {
  type_of_media: MediaType;
  preview: string;
  data: string;
  datetime: string;
}

interface ViolationSanctionDto extends Record{
  name: string;
}

export interface ViolationDto {
  id: string;
  image: string;
  title: string;
  cameraName: string;
  cameraId: ViolationCameraFilter;
  datetime: Date;
  endDatetime: Date;
  responsible_id: string;
  violatorsIds: Array<string>;
  status: ViolationStatus;
  type: ViolationType;
  media: ViolationMedia[];
  zoneId: string;
  zoneName: string;
  departmentId: string;
  departmentName: string;
  sanction: ViolationSanctionDto;
}

interface ViolationRawDto {
  id: string;
  image: string;
  title: string;
  cameraname: string;
  camera_id: ViolationCameraFilter;
  end_datetime: string;
  datetime: string;
  responsible_id: string;
  violators_ids: Array<string>;
  status: ViolationStatus;
  type: ViolationType;
  media: ViolationMediaRaw[];
  zone_id: string;
  zone_name: string;
  department_id: string;
  department_name: string;
  sanction: ViolationSanctionDto;
}

export interface ViolationHistoryItem {
  id: string;
  datetime: string;
  description: string;
  author: UserDto;
  status: ViolationStatus;
  rejectReason?: RejectReason;
}

interface ViolationHistoryItemDto {
  id: string;
  datetime: string;
  description: string;
  author: { id: string; username: string; first_name: string; last_name: string; groups: string[] };
  status: { id: string; name: string; code: ViolationStatus };
  reject_reason?: { id: string; name: string; code: RejectReason };
}

interface AcceptRequestDto {
  id: string;
  responsibleId: string;
  violatorsId: string[];
  sanctionId: string;
}

function convertRawToProcessed(raw: ViolationRawDto): ViolationDto {
  const violation: ViolationDto = {
    cameraName: raw.cameraname,
    cameraId: raw.camera_id,
    datetime: moment(raw.datetime, moment.ISO_8601).toDate(),
    endDatetime: moment(raw.end_datetime, moment.ISO_8601).toDate(),
    media: [],
    id: raw.id,
    type: raw.type,
    status: raw.status,
    image: raw.image,
    title: raw.title,
    violatorsIds: raw.violators_ids,
    responsible_id: raw.responsible_id,
    zoneId: raw.zone_id,
    zoneName: raw.zone_name,
    departmentId: raw.department_id,
    departmentName: raw.department_name,
    sanction: raw.sanction
  };
  violation.media = raw.media.map<ViolationMedia>(value => {
    return {
      data: value.data,
      preview: value.preview,
      type: value.type_of_media,
      datetime: moment(value.datetime, moment.ISO_8601).toDate()
    };
  });
  return violation;
}

function rawViolationsToProcessed(): OperatorFunction<PageableListResponse<ViolationRawDto>, PageableListResponse<ViolationDto>> {
  return map<PageableListResponse<ViolationRawDto>, PageableListResponse<ViolationDto>>(pageableResponse => {
    const convertedItems = pageableResponse.items.map(convertRawToProcessed);
    return {
      items: convertedItems,
      hasNext: pageableResponse.hasNext,
      hasPrevious: pageableResponse.hasPrevious
    };
  });
}

function rawViolationToProcessed(): OperatorFunction<ViolationRawDto, ViolationDto> {
  return map<ViolationRawDto, ViolationDto>(convertRawToProcessed);
}

@Injectable({
  providedIn: 'root'
})
export class ViolationsService extends ServiceBase {

  private refreshRequest$: Subject<void> = new Subject<void>();

  public constructor(
    private websocket: WebsocketService,
    backend: BackendService,
  ) {
    super(backend);
  }

  public get created$(): Observable<ViolationDto> {
    return this.websocket.getChannel<ViolationRawDto>('violations.add_new_violation')
      .pipe(rawViolationToProcessed());
  }

  public get updated$(): Observable<ViolationDto> {
    return this.websocket.getChannel<ViolationRawDto>('violations.violation_updated')
      .pipe(rawViolationToProcessed());
  }

  public get refresh$(): Observable<void> {
    return this.refreshRequest$.asObservable();
  }

  protected get object(): string {
    return 'violation';
  }

  public getCounters(
    type: ViolationTypeFilter = ViolationTypeFilter.all,
    camera_id: ViolationCameraFilter = 'all',
    department_id: ViolationDepartmentFilter = 'all',
    date_range: ViolationDateRangeFilter
  ): Observable<ViolationsCounter[]> {

    return this.makeGetCall<ViolationsCounter[]>('get-count', {
      type,
      camera_id,
      department_id,
      start_date: date_range.startDate.format('YYYY-MM-DD'),
      end_date: moment(date_range.endDate).add(1, 'day').format('YYYY-MM-DD')
    });
  }

  public getList(
    type: ViolationTypeFilter = ViolationTypeFilter.all,
    status: ViolationStatusFilter = ViolationStatusFilter.all,
    cameraId: ViolationCameraFilter = 'all',
    department_id: ViolationDepartmentFilter = 'all',
    date_range: ViolationDateRangeFilter,
    pageLoadConfig: PageLoadConfig
  ): Observable<PageableListResponse<ViolationDto>> {
    const args = {
      type: String(type),
      pageSize: String(pageLoadConfig.pageSize),
      before: pageLoadConfig.before,
      after: pageLoadConfig.after,
      status: String(status),
      camera_id: String(cameraId),
      department_id: String(department_id),
      start_date: date_range.startDate.format('YYYY-MM-DD'),
      end_date: moment(date_range.endDate).add(1, 'day').format('YYYY-MM-DD')
    };

    if (!args.after) {
      delete args.after;
    }

    if (!args.before) {
      delete args.before;
    }

    return this.makeGetCall<PageableListResponse<ViolationRawDto>>('', args)
      .pipe(rawViolationsToProcessed());
  }

  public acceptRequest(params: AcceptRequestDto): Observable<ViolationDto> {
    return this.makePostCall('accept-request', {
      id: params.id,
      responsible_id: params.responsibleId,
      violators_id: params.violatorsId[0] === null ? [] : params.violatorsId,
      sanction_id: params.sanctionId
    }).pipe(rawViolationToProcessed());
  }

  public declineRequest(violationId: string, reason: RejectReason, desc: string): Observable<ViolationDto> {
    return this.makePostCall('reject-request', {
      id: violationId,
      rejection_code: String(reason),
      comment: desc
    }).pipe(rawViolationToProcessed());
  }

  public approvalRequest(params: AcceptRequestDto): Observable<ViolationDto> {
    return this.makePostCall('approval-request', {
      id: params.id,
      responsible_id: params.responsibleId,
      violators_id: params.violatorsId[0] === null ? [] : params.violatorsId,
      sanction_id: params.sanctionId
    }).pipe(rawViolationToProcessed());
  }

  public getHistory(violationId: string): Observable<ViolationHistoryItem[]> {
    return this.makeGetCall<ViolationHistoryItemDto[]>('get-history', {violation: violationId}, 'history')
      .pipe(map<ViolationHistoryItemDto[], ViolationHistoryItem[]>(value => {
        return value.map(item => {
          return {
            id: item.id,
            author: {
              id: item.author.id,
              firstName: item.author.first_name,
              lastName: item.author.last_name,
              username: item.author.username
            },
            datetime: item.datetime,
            description: item.description,
            rejectReason: item.reject_reason && item.reject_reason.code,
            status: item.status && item.status.code
          };
        });
      }));
  }

  public makeTotalRefreshRequest(): void {
    this.refreshRequest$.next();
  }

  public export(
    type: ViolationTypeFilter = ViolationTypeFilter.all,
    status: ViolationStatusFilter = ViolationStatusFilter.all
  ): Observable<string> {
    const args = {
      type: String(type),
      status: String(status)
    };

    return this.makeGetCall<string>('export', args);
  }

  public get(violationId: string): Observable<ViolationDto> {
    return this.makeGetCall<ViolationRawDto>(violationId)
      .pipe(rawViolationToProcessed());
  }

  public removeViolation(violationId: string) {
    return this.makePostCall('mark-as-deleted', {
      id: violationId,
    });
  }
}
